import { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { useConfig } from '../../contexts/ConfigContext';
import { useLog } from '../../contexts/LogContext';
import { api } from '../../utils/api';
import { buildRecipientOptions } from '../../utils/buildRecipientOptions';
import type {
  AvailableMember,
  GetProjectResponse,
  GetTeamResponse,
  Language,
  NotifyReadyResponse,
  ProjectDetail,
  RecipientChoice,
  TeamMember,
} from './types';

const FROM_DISPLAY = 'Monco Team <contact@monco.ai>';
const CUSTOM_RECIPIENT: RecipientChoice = '__custom__';

const DEFAULTS: Record<Language, { subject: string; message: string }> = {
  en: {
    subject: '{projectName} — ready on Monco',
    message: `The project {projectName} has been processed and is now ready for review. You can access it via the link below.

If you have any questions, please reply to this email.

Best regards,
Monco Team`,
  },
  de: {
    subject: '{projectName} — bereit auf Monco',
    message: `Das Projekt {projectName} wurde verarbeitet und steht nun zur Prüfung bereit. Sie können es über den unten stehenden Link aufrufen.

Bei Fragen antworten Sie einfach auf diese E-Mail.

Mit freundlichen Grüßen,
Monco-Team`,
  },
};

const DEFAULT_LANGUAGE: Language = 'de';

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

type CreatorLookupArgs = {
  project: ProjectDetail | null;
  teamMembers: TeamMember[];
  availableMembers: AvailableMember[];
};

// Creator isn't auto-added to the team, so search the whole organization.
const findCreatorMember = ({
  project,
  teamMembers,
  availableMembers,
}: CreatorLookupArgs): TeamMember | AvailableMember | null => {
  const creatorEmail = project?.createdByEmail ?? null;
  if (!creatorEmail) return null;
  const normalizedCreatorEmail = creatorEmail.toLowerCase();
  return (
    [...teamMembers, ...availableMembers].find(
      (member) =>
        member.email.toLowerCase() === normalizedCreatorEmail ||
        (project?.createdBy != null && member.id === project.createdBy),
    ) ?? null
  );
};

export const SendEmailContainer = () => {
  const { token, projectId: defaultProjectId } = useConfig();
  const { log } = useLog();
  const requestIdRef = useRef(0);

  const [projectIdInput, setProjectIdInput] = useState(defaultProjectId);
  const [project, setProject] = useState<ProjectDetail | null>(null);
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);
  const [availableMembers, setAvailableMembers] = useState<AvailableMember[]>(
    [],
  );
  const [loadingMembers, setLoadingMembers] = useState(false);
  const [selectedRecipient, setSelectedRecipient] =
    useState<RecipientChoice>(CUSTOM_RECIPIENT);
  const [customEmail, setCustomEmail] = useState('');
  const [language, setLanguage] = useState<Language>(DEFAULT_LANGUAGE);
  const [subject, setSubject] = useState(DEFAULTS[DEFAULT_LANGUAGE].subject);
  const [messageBody, setMessageBody] = useState(
    DEFAULTS[DEFAULT_LANGUAGE].message,
  );
  const [sending, setSending] = useState(false);

  const createdByEmail = project?.createdByEmail ?? null;

  const creatorMember = useMemo(
    () => findCreatorMember({ project, teamMembers, availableMembers }),
    [project, teamMembers, availableMembers],
  );

  const isCreatorOrganizationMember = creatorMember !== null;

  const recipientOptions = useMemo(
    () => buildRecipientOptions({ creatorMember, teamMembers, availableMembers }),
    [creatorMember, teamMembers, availableMembers],
  );

  const trimmedProjectId = projectIdInput.trim();
  const numericProjectId = Number(trimmedProjectId);
  const projectIdValid =
    trimmedProjectId.length > 0 &&
    Number.isInteger(numericProjectId) &&
    numericProjectId > 0;

  const selectedRecipientEmail =
    selectedRecipient === CUSTOM_RECIPIENT
      ? customEmail.trim()
      : selectedRecipient;

  const hasSelectedRecipient = EMAIL_REGEX.test(selectedRecipientEmail);
  
  const canBulkSend =
    projectIdValid && !loadingMembers && recipientOptions.length > 0;

  const handleLanguageChange = useCallback((next: Language) => {
    setLanguage(next);
    setSubject(DEFAULTS[next].subject);
    setMessageBody(DEFAULTS[next].message);
  }, []);

  const loadProjectContext = useCallback(
    async (projectId: number, requestId: number) => {
      setLoadingMembers(true);
      try {
        const [projectResponse, teamResponse, availableResponse] =
          await Promise.all([
            api<GetProjectResponse>(
              'GET',
              `/api/projects/${projectId}`,
              undefined,
              token,
            ),
            api<GetTeamResponse>(
              'GET',
              `/api/projects/${projectId}/team`,
              undefined,
              token,
            ),
            api<AvailableMember[]>(
              'GET',
              `/api/projects/${projectId}/available-members`,
              undefined,
              token,
            ),
          ]);

        // Ignore if a newer project id was requested while this was in flight.
        if (requestIdRef.current !== requestId) return;

        let projectDetail: ProjectDetail | null = null;
        if (projectResponse.status === 200) {
          projectDetail = projectResponse.data.project;
          setProject(projectDetail);
        } else {
          setProject(null);
          log(
            'err',
            `Failed to load project #${projectId}: ${projectResponse.status} ${JSON.stringify(
              projectResponse.data,
            )}`,
          );
        }

        let fetchedTeamMembers: TeamMember[] = [];
        if (teamResponse.status === 200) {
          fetchedTeamMembers = teamResponse.data.teamMembers ?? [];
          setTeamMembers(fetchedTeamMembers);
        } else {
          setTeamMembers([]);
          log(
            'err',
            `Failed to load project team: ${teamResponse.status} ${JSON.stringify(
              teamResponse.data,
            )}`,
          );
        }

        let fetchedAvailableMembers: AvailableMember[] = [];
        if (
          availableResponse.status === 200 &&
          Array.isArray(availableResponse.data)
        ) {
          fetchedAvailableMembers = availableResponse.data;
          setAvailableMembers(fetchedAvailableMembers);
        } else {
          setAvailableMembers([]);
        }

        const resolvedCreatorMember = findCreatorMember({
          project: projectDetail,
          teamMembers: fetchedTeamMembers,
          availableMembers: fetchedAvailableMembers,
        });

        // Auto-select the project creator; otherwise fall back to a custom email.
        if (resolvedCreatorMember) {
          setSelectedRecipient(resolvedCreatorMember.email);
          log(
            'info',
            `Auto-selected project creator (${resolvedCreatorMember.email}) for project #${projectId}`,
          );
        } else {
          setSelectedRecipient(CUSTOM_RECIPIENT);
          log(
            'warn',
            `Project #${projectId} has no resolvable creator — use a custom email`,
          );
        }
      } catch (error) {
        if (requestIdRef.current !== requestId) return;
        setProject(null);
        setTeamMembers([]);
        setAvailableMembers([]);
        setSelectedRecipient(CUSTOM_RECIPIENT);
        log(
          'err',
          `Error loading project context: ${(error as Error).message}`,
        );
      } finally {
        if (requestIdRef.current === requestId) setLoadingMembers(false);
      }
    },
    [token, log],
  );

  const sendToRecipient = useCallback(
    async (recipient: string): Promise<boolean> => {
      try {
        const response = await api<NotifyReadyResponse>(
          'POST',
          `/api/projects/${numericProjectId}/notify-ready`,
          {
            to: recipient,
            subject: subject.trim(),
            messageBody,
            language,
          },
          token,
        );

        if (response.status === 200 || response.status === 201) {
          log(
            'ok',
            `Email sent to ${recipient} (messageId: ${response.data.messageId})`,
          );
          return true;
        }

        log(
          'err',
          `Failed to send to ${recipient}: ${response.status} ${JSON.stringify(
            response.data,
          )}`,
        );
        return false;
      } catch (error) {
        log('err', `Error sending to ${recipient}: ${(error as Error).message}`);
        return false;
      }
    },
    [numericProjectId, subject, messageBody, language, token, log],
  );

  const validateEmailContent = useCallback((): boolean => {
    if (!projectIdValid) {
      log('err', 'Enter a valid project ID first');
      return false;
    }
    if (!subject.trim()) {
      log('err', 'Subject is required');
      return false;
    }
    if (!messageBody.trim()) {
      log('err', 'Message body is required');
      return false;
    }
    return true;
  }, [projectIdValid, subject, messageBody, log]);

  const handleSend = useCallback(async () => {
    if (!validateEmailContent()) return;

    const recipient =
      selectedRecipient === CUSTOM_RECIPIENT
        ? customEmail.trim()
        : selectedRecipient;

    if (!recipient || !EMAIL_REGEX.test(recipient)) {
      log('err', 'Recipient email is missing or invalid');
      return;
    }

    setSending(true);
    try {
      await sendToRecipient(recipient);
    } finally {
      setSending(false);
    }
  }, [validateEmailContent, selectedRecipient, customEmail, sendToRecipient, log]);

  const handleBulkSend = useCallback(async () => {
    if (!validateEmailContent()) return;

    const recipients = recipientOptions
      .map((option) => option.email)
      .filter((email) => EMAIL_REGEX.test(email));

    if (recipients.length === 0) {
      log('err', 'No project recipients to send to');
      return;
    }

    const confirmed = window.confirm(
      `Send this email individually to all ${recipients.length} recipient(s)?\n\n${recipients.join(
        '\n',
      )}`,
    );
    
    if (!confirmed) return;

    setSending(true);
    try {
      let sentCount = 0;
      for (const recipient of recipients) {
        const succeeded = await sendToRecipient(recipient);
        if (succeeded) sentCount += 1;
      }
      const failedCount = recipients.length - sentCount;
      log(
        failedCount === 0 ? 'ok' : 'warn',
        failedCount === 0
          ? `Bulk send finished: ${sentCount} sent.`
          : `Bulk send finished: ${sentCount} sent, ${failedCount} failed (of ${recipients.length}).`,
      );
    } finally {
      setSending(false);
    }
  }, [validateEmailContent, recipientOptions, sendToRecipient, log]);

  useEffect(() => {
    const requestId = ++requestIdRef.current;
    if (!projectIdValid) {
      setProject(null);
      setTeamMembers([]);
      setAvailableMembers([]);
      setSelectedRecipient(CUSTOM_RECIPIENT);
      setLoadingMembers(false);
      return;
    }
    loadProjectContext(numericProjectId, requestId);
  }, [numericProjectId, projectIdValid, loadProjectContext]);

  return (
    <div className="bg-[#161b22] border border-[#30363d] rounded-lg p-4 mb-4">
      <h2 className="text-sm text-[#8b949e] uppercase tracking-wide mb-3">
        Send Project-Ready Email
      </h2>

      <div className="mb-3">
        <label className="block text-xs text-[#8b949e] mb-1">From</label>
        <div className="bg-[#0d1117] border border-[#30363d] rounded px-3 py-2 text-[#c9d1d9]">
          {FROM_DISPLAY}
        </div>
      </div>

      <div className="mb-3">
        <label className="block text-xs text-[#8b949e] mb-1">Project ID</label>
        <input
          type="number"
          value={projectIdInput}
          onChange={(e) => setProjectIdInput(e.target.value)}
          className="w-full bg-[#0d1117] border border-[#30363d] rounded px-3 py-2 text-[#c9d1d9] focus:outline-none focus:border-[#58a6ff]"
          placeholder="e.g. 61"
        />
      </div>

      <div className="mb-3">
        <label className="block text-xs text-[#8b949e] mb-1">
          To (Recipient)
        </label>
        <select
          value={selectedRecipient}
          onChange={(e) => setSelectedRecipient(e.target.value)}
          disabled={loadingMembers}
          className="w-full bg-[#0d1117] border border-[#30363d] rounded px-3 py-2 text-[#c9d1d9] focus:outline-none focus:border-[#58a6ff] disabled:opacity-50"
        >
          {recipientOptions.map((option) => (
            <option key={option.key} value={option.email}>
              {option.label}
            </option>
          ))}
          <option value={CUSTOM_RECIPIENT}>Custom email…</option>
        </select>
        {loadingMembers && (
          <p className="text-xs text-[#8b949e] mt-1">Loading team members…</p>
        )}
        {!loadingMembers && projectIdValid && recipientOptions.length === 0 && (
          <p className="text-xs text-[#d29922] mt-1">
            No project recipients found. Use a custom email.
          </p>
        )}
        {!loadingMembers && projectIdValid && createdByEmail && (
          <p className="text-xs text-[#8b949e] mt-1">
            Created by:{' '}
            <span className="text-[#c9d1d9]">{createdByEmail}</span>
            {!isCreatorOrganizationMember && (
              <span className="text-[#8b949e]"> (no longer in organization)</span>
            )}
          </p>
        )}
      </div>

      {selectedRecipient === CUSTOM_RECIPIENT && (
        <div className="mb-3">
          <label className="block text-xs text-[#8b949e] mb-1">
            Custom email
          </label>
          <input
            type="email"
            value={customEmail}
            onChange={(e) => setCustomEmail(e.target.value)}
            className="w-full bg-[#0d1117] border border-[#30363d] rounded px-3 py-2 text-[#c9d1d9] focus:outline-none focus:border-[#58a6ff]"
            placeholder="customer@example.com"
          />
        </div>
      )}

      <div className="mb-3">
        <label className="block text-xs text-[#8b949e] mb-2">Language</label>
        <div className="flex items-center gap-4 flex-wrap">
          <label className="flex items-center gap-2 cursor-pointer text-[13px] text-[#c9d1d9]">
            <input
              type="radio"
              name="email-language"
              value="en"
              checked={language === 'en'}
              onChange={() => handleLanguageChange('en')}
              className="accent-[#58a6ff]"
            />
            English
          </label>
          <label className="flex items-center gap-2 cursor-pointer text-[13px] text-[#c9d1d9]">
            <input
              type="radio"
              name="email-language"
              value="de"
              checked={language === 'de'}
              onChange={() => handleLanguageChange('de')}
              className="accent-[#58a6ff]"
            />
            Deutsch
          </label>
          <p className="text-xs text-[#8b949e] ml-2">
            Switching the language replaces the Subject and Message with the
            matching defaults.
          </p>
        </div>
      </div>

      <div className="mb-3">
        <label className="block text-xs text-[#8b949e] mb-1">Subject</label>
        <input
          type="text"
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
          maxLength={200}
          className="w-full bg-[#0d1117] border border-[#30363d] rounded px-3 py-2 text-[#c9d1d9] focus:outline-none focus:border-[#58a6ff]"
        />
      </div>

      <div className="mb-3">
        <label className="block text-xs text-[#8b949e] mb-1">
          Message (the project link is added automatically by the backend)
        </label>
        <textarea
          value={messageBody}
          onChange={(e) => setMessageBody(e.target.value)}
          maxLength={5000}
          rows={10}
          className="w-full bg-[#0d1117] border border-[#30363d] rounded px-3 py-2 text-[#c9d1d9] focus:outline-none focus:border-[#58a6ff] font-mono"
        />
        <p className="text-xs text-[#8b949e] mt-1">
          {'{projectName}'} in the subject or message will be replaced with the
          actual project name on send.
        </p>
      </div>
      <div className="flex items-center gap-3 flex-wrap">
        <button
          onClick={handleSend}
          disabled={
            sending || !projectIdValid || loadingMembers || !hasSelectedRecipient
          }
          className="bg-[#238636] hover:bg-[#2ea043] disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm font-semibold px-4 py-2 rounded"
        >
          {sending ? 'Sending…' : 'Send to Selected Member'}
        </button>
        {canBulkSend && (
          <button
            onClick={handleBulkSend}
            disabled={sending}
            className="bg-[#1f6feb] hover:bg-[#388bfd] disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm font-semibold px-4 py-2 rounded"
          >
            {sending ? 'Sending…' : 'Send to All Members'}
          </button>
        )}
      </div>
      {canBulkSend && (
        <p className="text-xs text-[#8b949e] mt-2">
          You can send this email to the selected member only, or to all project
          members. When sending to all, each recipient (organization admins, the
          project creator, and project members) receives a separate email. The
          custom email is not included in the “Send to all” action.
        </p>
      )}
    </div>
  );
};
